import { $createLinkNode, $toggleLink } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $createTextNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  PASTE_COMMAND,
} from "lexical";
import { useEffect } from "react";
import { useFlag } from "../../../../server/optimizely";
import { $isDesignHuddleNode } from "../nodes/design-huddle-node";
import { $isImageNode } from "../nodes/image-node";
import { $createSurveyLinkNode, $isSurveyLinkNode } from "../nodes/survey-link-node";
import {
  INSERT_LINK_COMMAND,
  INSERT_SURVEY_LINK_COMMAND,
  REMOVE_LINK_COMMAND,
} from "../types/commands";
import { urlRegex } from "../utils/link-matchers";
import { $multipleElementsSelected } from "../utils/multiple-elements-selected";
import { $selectionOrStart } from "../utils/selection-or-start";
import { linkButtonSelectionType } from "./toolbar/hooks/use-link-button-on-click";

export function LinkPlugin({ format }: { format: "email" | "chat" | "sms" }): null {
  const [editor] = useLexicalComposerContext();
  const [editor_focus_fix] = useFlag("cha_4472_editor_focus_fix_for_links_and_surveys");

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          INSERT_SURVEY_LINK_COMMAND,
          ({ title, url }) => {
            const currentSelection = editor_focus_fix ? $selectionOrStart() : $getSelection();

            const selectionType = linkButtonSelectionType(currentSelection);

            if (!currentSelection || selectionType === null || $multipleElementsSelected()) {
              return false;
            }
            // A new survey link (currently selecting text or cursor in empty space)
            if (selectionType === "range") {
              const linkText = title?.trim() || currentSelection.getTextContent() || "survey_link";
              $insertNodes([$createSurveyLinkNode(url).append($createTextNode(linkText))]);
            }
            // updated survey link (currently selecting a survey link)
            if (selectionType === "survey_link") {
              const node = currentSelection.getNodes()?.[0];
              const surveyNode = node?.getParent();
              if ($isSurveyLinkNode(surveyNode)) {
                surveyNode.setURL(url);
              }
            }

            return true;
          },
          COMMAND_PRIORITY_LOW,
        ),
        editor.registerCommand(
          INSERT_LINK_COMMAND,
          ({ title, url }) => {
            if (format === "sms") return false;

            const currentSelection = editor_focus_fix ? $selectionOrStart() : $getSelection();

            const selectionType = linkButtonSelectionType(currentSelection);

            if (!currentSelection || selectionType === null || $multipleElementsSelected()) {
              return false;
            }

            // In case somehow we trigger add link when already on a link.
            if (selectionType === "survey_link") {
              $insertNodes([$createSurveyLinkNode(url)]);
            }

            if (selectionType === "range") {
              title
                ? $insertNodes([
                    $createLinkNode(url).append($createTextNode(title)),
                    $createTextNode(""),
                  ])
                : $toggleLink(url);
            }

            if (selectionType === "image") {
              // must be the first node
              const node = currentSelection.getNodes()[0];
              if ($isImageNode(node) || $isDesignHuddleNode(node)) node.setHref(url);
            }

            return true;
          },
          COMMAND_PRIORITY_LOW,
        ),
        editor.registerCommand(
          REMOVE_LINK_COMMAND,
          () => {
            const currentSelection = $getSelection();

            const selectionType = linkButtonSelectionType(currentSelection);

            if (!currentSelection || selectionType === null || $multipleElementsSelected())
              return false;

            if (selectionType === "range") $toggleLink(null);

            if (selectionType === "image") {
              // must be the first node
              const node = currentSelection.getNodes()[0];
              if ($isImageNode(node) || $isDesignHuddleNode(node)) node.setHref();
            }

            if (selectionType === "survey_link") {
              const node = currentSelection.getNodes()[0]!;
              if ($isSurveyLinkNode(node.getParent())) {
                node.getParent()?.remove();
              }
            }

            return true;
          },
          COMMAND_PRIORITY_LOW,
        ),
        editor.registerCommand(
          PASTE_COMMAND,
          (data) => {
            if (format === "sms") return false;

            // If the clipboard data contains a URL, insert a link node
            const currentSelection = $getSelection();
            if (!$isRangeSelection(currentSelection) || $multipleElementsSelected()) return false;

            // If nothing is really selected, don't do anything
            if (currentSelection.getTextContent().trim().length === 0) return false;

            if (!(data instanceof ClipboardEvent)) return false;
            const pastedText = data.clipboardData?.getData("text/plain");

            if (!pastedText) return false;

            if (pastedText.match(urlRegex)) {
              editor.dispatchCommand(INSERT_LINK_COMMAND, { url: pastedText });
              return true;
            }
            return false;
          },
          COMMAND_PRIORITY_HIGH,
        ),
      ),
    [editor, format, editor_focus_fix],
  );

  return null;
}
